import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Box,
   Text,
   Flex,
   useColorModeValue,
   ModalFooter,
   GridItem,
   Grid,
   ModalBody,
   ModalCloseButton,
   ModalHeader,
   ModalContent,
   ModalOverlay,
   Modal,
   useDisclosure,
   Heading,
   VStack,
   Icon,
   Image,
   useColorMode,
   Skeleton,
   HStack,
} from '@chakra-ui/react';
import { Settings, Users, Printer, Building2, Share2, QrCode, Sliders } from 'lucide-react';
import CompanySettingForm from '../companyProfile/CompanySettingForm';
import { useUser, useGlobal } from 'contexts';
import { getWpQrCode } from '../../../redux/actions/settings';
import { useDispatch, useSelector } from 'react-redux';
import SharedCompForm from '../companyProfile/SharedCompForm';
import CompanyForm from '../companyProfile/CompanyForm';
import QrCodeModal from './QrCodesetting';

const profilesData = {
   companySettings: [
      {
         id: 9,
         title: 'Company Profile',
         description: 'Manage your company information',
         icon: Building2,
         gradient: 'linear(to-br, blue.500, cyan.500)',
         adminOnly: false,
      },
      {
         id: 2,
         title: 'Company Preference',
         description: 'Configure company-specific preferences',
         icon: Settings,
         gradient: 'linear(to-br, purple.500, pink.500)',
         adminOnly: false,
      },
      {
         id: 8,
         title: 'Company Share',
         description: 'Share company data and resources',
         icon: Share2,
         gradient: 'linear(to-br, green.500, teal.500)',
         adminOnly: true,
      },
   ],
   globalSettings: [
      {
         id: 3,
         path: '/admin/setting/manageusers',
         title: 'Manage Users',
         description: 'Control user accounts and permissions',
         icon: Users,
         gradient: 'linear(to-br, orange.500, red.500)',
         adminOnly: true,
      },
      {
         id: 4,
         path: '/admin/setting/print-settings',
         title: 'Print Settings',
         description: 'Customize print formats and templates',
         icon: Printer,
         gradient: 'linear(to-br, indigo.500, purple.500)',
         adminOnly: false,
      },
      {
         id: 6,
         path: '/admin/setting/global-settings',
         title: 'Company Wise Master Settings',
         description: 'Manage dropdown visibility across masters',
         icon: Sliders,
         gradient: 'linear(to-br, teal.500, cyan.500)',
         adminOnly: false,
      },
      {
         id: 7,
         path: '/admin/setting/qrcode',
         title: 'QR Code',
         description: 'Scan QR code to connect with WhatsApp',
         icon: QrCode,
         gradient: 'linear(to-br, pink.500, rose.500)',
         adminOnly: true,
      },
   ],
};

// Skeleton Loader Components
const CardSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const bgColor = useColorModeValue('white', 'gray.800');
   const borderColor = useColorModeValue('gray.200', 'gray.600');

   return (
      <Box
         position="relative"
         width={{
            base: 'calc(100% - 8px)',
            sm: 'calc(90% - 10px)',
            md: 'calc(50% - 14px)',
            lg: '400px',
            xl: '320px',
         }}
         minWidth={{ base: '140px', sm: '180px', md: '220px' }}
         borderWidth="1px"
         borderColor={borderColor}
         borderRadius={{ base: 'xl', md: '2xl' }}
         overflow="hidden"
         boxShadow="lg"
         p={{ base: 4, sm: 5, md: 6 }}
         bg={bgColor}>
         {/* Icon Skeleton */}
         <Skeleton
            width={{ base: '56px', md: '64px' }}
            height={{ base: '56px', md: '64px' }}
            borderRadius="xl"
            mb={{ base: 3, md: 4 }}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />

         {/* Title Skeleton */}
         <Skeleton
            height="24px"
            width="80%"
            mb={{ base: 2, md: 3 }}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />

         {/* Description Skeleton */}
         <VStack spacing="2" align="start" mb={2}>
            <Skeleton
               height="16px"
               width="100%"
               startColor={isDark ? 'gray.600' : 'gray.200'}
               endColor={isDark ? 'gray.700' : 'gray.300'}
            />
            <Skeleton
               height="16px"
               width="70%"
               startColor={isDark ? 'gray.600' : 'gray.200'}
               endColor={isDark ? 'gray.700' : 'gray.300'}
            />
         </VStack>

         {/* Arrow Skeleton */}
         <Skeleton
            width="20px"
            height="20px"
            borderRadius="md"
            position="absolute"
            bottom={{ base: 4, md: 6 }}
            right={{ base: 4, md: 6 }}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
      </Box>
   );
};

const HeaderSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Box mb={{ base: 8, md: 12 }}>
         <Skeleton
            height="32px"
            width="120px"
            mb={3}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <Skeleton
            height="20px"
            width="200px"
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
      </Box>
   );
};

const SectionHeaderSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Flex alignItems="center" gap={3} mb={{ base: 4, md: 6 }}>
         <Skeleton
            width="4px"
            height="32px"
            borderRadius="full"
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <Skeleton
            height="24px"
            width="150px"
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
      </Flex>
   );
};

const Card = () => {
   const { user } = useUser();
   const dispatch = useDispatch();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const { userGlobalSettingsData } = useGlobal();
   const navigate = useNavigate();
   const {
      isOpen: isOpenCompanySetting,
      onOpen: onOpenCompanySetting,
      onClose: onCloseCompanySetting,
   } = useDisclosure();
   const {
      isOpen: isOpenShareCompanySetting,
      onOpen: onOpenShareCompanySetting,
      onClose: onCloseShareCompanySetting,
   } = useDisclosure();
   const { isOpen: isOpenQrCode, onOpen: onOpenQrCode, onClose: onCloseQrCode } = useDisclosure();
   const { isOpen: isOpenCompanyModal, onOpen: onOpenCompanyModal, onClose: onCloseCompanyModal } = useDisclosure();
   const { company } = useGlobal();
   const qrcode = useSelector((state) => state.settings.wpQrCode);
   const globalSetting = userGlobalSettingsData?.[0]?.companyWiseWp;
   const shouldSendCompanyId = globalSetting || false;
   const companyId = user?.loginhistory?.companyId;

   const scrollbarColor = useColorModeValue('gray.300', 'gray.600');
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [isLoading, setIsLoading] = useState(false);
   const [selectedPath, setSelectedPath] = useState('');
   const [isPageLoading, setIsPageLoading] = useState(true);
   const companyType = localStorage.getItem("selectedCompanyType");
   const companyRight = Array.isArray(user?.userRights) ? user.userRights.find((right) => right.companyId === companyId)?.companyprofile : null;

   // Remove manual timeout and set loading to false immediately
   useEffect(() => {
      setIsPageLoading(false);
   }, []);

   // Dark mode color values
   const bgColor = useColorModeValue('white', 'gray.800');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const textColor = useColorModeValue('gray.800', 'gray.100');
   const descriptionColor = useColorModeValue('gray.600', 'gray.400');
   const headingColor = useColorModeValue('gray.800', 'white');
   const subHeadingColor = useColorModeValue('gray.600', 'gray.300');
   const modalBgColor = useColorModeValue('white', 'gray.800');
   const qrBoxBg = useColorModeValue('gray.100', 'gray.700');
   const qrBoxBorder = useColorModeValue('gray.200', 'gray.600');

   const handleNavigation = async (id, path) => {
      if (isPageLoading) return; // Prevent clicks during loading

      // Handle Company Setting card (id: 2)
      if (id === 2) {
         onOpenCompanySetting();
         return;
      }

      // Handle Company Share card (id: 8)
      if (id === 8) {
         onOpenShareCompanySetting();
         return;
      }

      // Handle Company Profile card (id: 9)
      if (id === 9) {
         onOpenCompanyModal();
         return;
      }

      if (id === 7) {
         onOpenQrCode(); // Use the correct function
         return;
      }
      setSelectedPath(path);

      // if (path === '/admin/setting/qrcode') {
      //    onOpen();
      //    setIsLoading(true);
      //    await dispatch(getWpQrCode({ ...(shouldSendCompanyId && { companyId: user?.loginhistory?.companyId }) }));
      //    setIsLoading(false);
      // } else {
      //    // Use React Router navigate for client-side navigation (no page refresh)
      //    navigate(path);
      // }
      navigate(path);

      // For other navigation paths
   };

   const renderCards = (profiles) => (
      <Flex
         gap={{ base: 4, md: 5, lg: 6 }}
         wrap="wrap"
         justify={{ base: 'center', md: 'flex-start' }}
         align="flex-start"
         width="100%">
         {profiles.map(({ id, path, title, description, icon: IconComponent, gradient, adminOnly }) => {
            // Skip admin-only cards for non-admin users
            if (adminOnly && !user?.isAdmin) {
               return null;
            }

            const share_company = user?.packageData?.[0]?.share_company || false;
            const company_wise_master = user?.packageData?.[0]?.company_wise_master || false;

            if (id === 8 && !share_company) {
               return null;
            }

            if (id === 6 && !company_wise_master) {
               return null;
            }
            const compView = user?.isAdmin || companyRight?.edit;
            if ((id === 2 || id === 9 || id === 6) && !compView) {
               return null;
            }

            return (
               <Box
                  key={id}
                  position="relative"
                  width={{
                     base: 'calc(100% - 8px)',
                     sm: 'calc(90% - 10px)',
                     md: 'calc(50% - 14px)',
                     lg: '400px',
                     xl: '320px',
                  }}
                  minWidth={{ base: '140px', sm: '180px', md: '220px' }}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius={{ base: 'xl', md: '2xl' }}
                  overflow="hidden"
                  boxShadow="lg"
                  p={{ base: 4, sm: 5, md: 6 }}
                  bg={bgColor}
                  opacity={selectedPath === path ? 0.7 : 1}
                  transform={selectedPath === path ? 'scale(0.95)' : 'scale(1)'}
                  _hover={{
                     boxShadow: '2xl',
                     transform:
                        selectedPath === path
                           ? 'scale(0.95)'
                           : { base: 'scale(1.02) translateY(-4px)', md: 'scale(1.05) translateY(-4px)' },
                     '& .gradient-bg': {
                        opacity: 0.1,
                     },
                     '& .icon-box': {
                        transform: 'scale(1.1)',
                     },
                     '& .arrow-icon': {
                        opacity: 1,
                        transform: 'translateX(0)',
                     },
                  }}
                  onClick={() => handleNavigation(id, path)}
                  cursor="pointer"
                  transition="all 0.3s ease-out">
                  {/* Gradient Background Overlay */}
                  <Box
                     className="gradient-bg"
                     position="absolute"
                     inset="0"
                     bgGradient={gradient}
                     opacity={0}
                     transition="opacity 0.3s"
                  />

                  {/* Icon */}
                  <Box
                     className="icon-box"
                     position="relative"
                     w={{ base: '14', md: '16' }}
                     h={{ base: '14', md: '16' }}
                     borderRadius="xl"
                     display="flex"
                     alignItems="center"
                     justifyContent="center"
                     boxShadow="lg"
                     mb={{ base: 3, md: 4 }}
                     transition="transform 0.3s"
                     sx={{
                        background: gradient.includes('indigo')
                           ? 'linear-gradient(to bottom right, #6366f1, #a855f7)'
                           : gradient.includes('pink') && gradient.includes('rose')
                              ? 'linear-gradient(to bottom right, #ec4899, #f43f5e)'
                              : undefined,
                        bgGradient:
                           gradient.includes('indigo') || (gradient.includes('pink') && gradient.includes('rose'))
                              ? undefined
                              : gradient,
                     }}>
                     <Icon as={IconComponent} boxSize={{ base: 7, md: 8 }} color="white" strokeWidth={2} />
                  </Box>

                  {/* Content */}
                  <Box position="relative">
                     <Text
                        fontSize={{ base: 'md', md: 'lg' }}
                        fontWeight="bold"
                        color={textColor}
                        mb={{ base: 1, md: 2 }}
                        lineHeight="1.3"
                        noOfLines={2}>
                        {title}
                     </Text>
                     <Text fontSize={{ base: 'xs', sm: 'sm' }} color={descriptionColor} lineHeight="1.5" noOfLines={3}>
                        {description}
                     </Text>
                  </Box>

                  {/* Hover Arrow */}
                  <Box
                     className="arrow-icon"
                     position="absolute"
                     bottom={{ base: 4, md: 6 }}
                     right={{ base: 4, md: 6 }}
                     opacity={0}
                     transform="translateX(8px)"
                     transition="all 0.3s">
                     <svg
                        width="20"
                        height="20"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        color={descriptionColor}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                  </Box>
               </Box>
            );
         })}
      </Flex>
   );

   const renderSkeletonCards = (count) => (
      <Flex
         gap={{ base: 4, md: 5, lg: 6 }}
         wrap="wrap"
         justify={{ base: 'center', md: 'flex-start' }}
         align="flex-start"
         width="100%">
         {Array.from({ length: count }).map((_, index) => (
            <CardSkeleton key={index} />
         ))}
      </Flex>
   );

   return (
      <>
         <Box
            height="100vh"
            width="100%"
            overflowY="auto"
            overflowX="hidden"
            sx={{
               '&::-webkit-scrollbar': {
                  width: { base: '4px', md: '6px' },
               },
               '&::-webkit-scrollbar-track': {
                  background: 'transparent',
               },
               '&::-webkit-scrollbar-thumb': {
                  background: scrollbarColor,
                  borderRadius: '10px',
               },
               '&::-webkit-scrollbar-thumb:hover': {
                  background: useColorModeValue('gray.400', 'gray.500'),
               },
               scrollbarWidth: 'thin',
               scrollbarColor: `${scrollbarColor} transparent`,
            }}
            pt={{ base: '60px', md: '30px', lg: '50px' }}
            px={{ base: 3, md: 4, lg: 6 }}
            pb={{ base: '20px', md: '30px' }}>
            {/* Header */}
            {isPageLoading ? (
               <HeaderSkeleton />
            ) : (
               <Box mb={{ base: 8, md: 12 }}>
                  <Heading
                     as="h1"
                     fontSize={{ base: '2xl', md: '4xl' }}
                     fontWeight="bold"
                     color={headingColor}
                     mb={{ base: 2, md: 3 }}>
                     Settings
                  </Heading>
                  <Text fontSize={{ base: 'sm', md: 'md' }} color={subHeadingColor}>
                     Manage your company and system preferences
                  </Text>
               </Box>
            )}

            <VStack spacing={{ base: 10, md: 12, lg: 16 }} align="stretch" width="100%">
               {/* Company Settings Section */}
               {companyType === "regular" && (user?.isAdmin || companyRight?.edit) && (
                  <Box>
                     {isPageLoading ? (
                        <SectionHeaderSkeleton />
                     ) : (
                        <Flex alignItems="center" gap={3} mb={{ base: 4, md: 6 }}>
                           <Box w="1" h="8" bgGradient="linear(to-b, blue.500, purple.500)" borderRadius="full" />
                           <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={headingColor}>
                              Company Settings
                           </Heading>
                        </Flex>
                     )}

                     {isPageLoading
                        ? renderSkeletonCards(profilesData.companySettings.length)
                        : renderCards(profilesData.companySettings)}
                  </Box>
               )}

               {/* Global Settings Section */}
               <Box>
                  {isPageLoading ? (
                     <SectionHeaderSkeleton />
                  ) : (
                     <Flex alignItems="center" gap={3} mb={{ base: 4, md: 6 }}>
                        <Box w="1" h="8" bgGradient="linear(to-b, green.500, teal.500)" borderRadius="full" />
                        <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={headingColor}>
                           Global Settings
                        </Heading>
                     </Flex>
                  )}

                  {isPageLoading
                     ? renderSkeletonCards(
                        profilesData.globalSettings.filter((profile) => !profile.adminOnly || user?.isAdmin).length,
                     )
                     : renderCards(profilesData.globalSettings)}
               </Box>
            </VStack>

            <Box height={{ base: '60px', md: '40px' }} />
         </Box>

         <QrCodeModal isOpen={isOpenQrCode} onClose={onCloseQrCode} isLoading={isLoading} />

         {/* Company Setting Form Modal */}
         <CompanySettingForm
            isOpen={isOpenCompanySetting}
            onClose={onCloseCompanySetting}
            isEditMode={true}
            currentRow={company}
         />

         {/* Company Share Form Modal */}
         <SharedCompForm
            isOpen={isOpenShareCompanySetting}
            onClose={onCloseShareCompanySetting}
            isEditMode={true}
            selectedSharedComp={company?.id}
            currentRow={company && company?.id ? company : null}
         />

         {/* Company Profile Form Modal */}
         <CompanyForm
            isOpen={isOpenCompanyModal}
            onClose={onCloseCompanyModal}
            isEditMode={true}
            currentRow={company && company?.id ? company : null}
         />
      </>
   );
};

export default Card;
